import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { TOUR_STEPS } from './help-texts';
import { useUiStore } from '@/store/ui.store';
import { ESCRITORIO } from '@/hooks/useEsEscritorio';

/**
 * Tour guiado de primera vez (driver.js, MIT). Recorre el menú lateral
 * explicando el flujo diario del dueño. Se auto-lanza UNA vez por navegador al
 * primer login de un ADMIN (flag en localStorage) y se puede repetir desde el
 * menú de Ayuda del header.
 */
export const TOUR_DONE_KEY = 'conversia.tour.v1.done';

/** ¿Debe auto-lanzarse el tour para este usuario? (solo ADMIN, una vez). */
export function shouldAutoStartTour(role: string | undefined): boolean {
  if (role !== 'ADMIN') return false;
  try {
    return localStorage.getItem(TOUR_DONE_KEY) !== '1';
  } catch {
    return false;
  }
}

function markTourDone(): void {
  try {
    localStorage.setItem(TOUR_DONE_KEY, '1');
  } catch {
    // sin localStorage (modo privado extremo): el tour simplemente se repetirá
  }
}

/** El primero de esos elementos que se ve (el lateral de escritorio y el del teléfono comparten marcas). */
function visible(selector: string): HTMLElement | undefined {
  return [...document.querySelectorAll<HTMLElement>(selector)].find((el) => el.getClientRects().length > 0);
}

/** El tour que espera a que entre el menú del teléfono (uno solo a la vez). */
let pendiente: ReturnType<typeof setTimeout> | undefined;

/**
 * Lanza el tour. Al terminar o saltarlo, queda marcado como visto.
 *
 * En el teléfono el menú vive detrás de la hamburguesa: se abre primero (y se cierra al
 * terminar), porque si no el tour señala un lateral escondido y se queda en blanco.
 */
export function startTour(): void {
  const telefono = !window.matchMedia(ESCRITORIO).matches;
  if (telefono) useUiStore.getState().abrirMenuMovil();
  // El menú del teléfono tarda 200 ms en entrar: se espera a que esté en su sitio. Si lo
  // lanzan dos veces seguidas, arranca uno solo.
  clearTimeout(pendiente);
  pendiente = setTimeout(() => arrancar(telefono), telefono ? 260 : 0);
}

function arrancar(telefono: boolean): void {
  const cerrarMenu = () => {
    if (telefono) useUiStore.getState().cerrarMenuMovil();
  };
  // Solo los pasos de lo que ESTA persona tiene en su menú y se ve en su pantalla: a la
  // clienta de una tienda no se le explica «Trabajos», «Tracking» ni «Instalación».
  const pasos = TOUR_STEPS.flatMap((s) => {
    const el = visible(s.element);
    return el ? [{ element: el, popover: { title: s.title, description: s.description } }] : [];
  });
  if (pasos.length === 0) return cerrarMenu();
  const d = driver({
    showProgress: true,
    progressText: '{{current}} de {{total}}',
    nextBtnText: 'Siguiente',
    prevBtnText: 'Anterior',
    doneBtnText: 'Listo',
    // Cerrar con la X o con Esc = saltar: también cuenta como visto.
    onDestroyed: () => {
      markTourDone();
      cerrarMenu();
    },
    steps: pasos,
  });
  d.drive();
}
